import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useOrganization } from '@clerk/clerk-react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../lib/api';
import { toast } from 'sonner';
import {
  MessageSquare, RefreshCw, Settings, CheckCircle2, Bot,
  AlertTriangle, Clock, Send, X, Package, Truck, Hash,
  Calendar, Mail, User, Download, ChevronRight, Circle,
  Wifi, WifiOff, AlertCircle, Search, Lock, RotateCcw,
  Eye, CheckSquare, Square,
} from 'lucide-react';
import { usePresence, getOtherViewers } from '../hooks/usePresence';
import { ViewersStack, ViewersBanner } from '../components/ViewersIndicator';
import { CustomerThreadsBanner } from '../components/CustomerThreadsBanner';

// ── Helpers ───────────────────────────────────────────────────

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return 'ahora';
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return `${Math.floor(diff / 86400)}d`;
}

function Avatar({ name, size = 'md' }) {
  const letter = (name || '?')[0].toUpperCase();
  const colors = ['bg-violet-600', 'bg-blue-600', 'bg-emerald-600', 'bg-amber-600', 'bg-rose-600'];
  const color = colors[letter.charCodeAt(0) % colors.length];
  const sz = size === 'sm' ? 'w-7 h-7 text-xs' : 'w-9 h-9 text-sm';
  return (
    <div className={`${sz} rounded-full ${color} flex items-center justify-center text-white font-semibold flex-shrink-0`}>
      {letter}
    </div>
  );
}

// ── Thread list item ──────────────────────────────────────────

function ThreadCard({ thread, selected, onClick, otherViewers, checked, onToggleCheck, showCheckbox }) {
  const name = thread.customer_name || thread.customer_email || 'Cliente';
  const lastMsg = thread.last_message;
  const preview = lastMsg?.message || lastMsg?.agent_reply || '—';
  const isAgent = lastMsg?.is_agent_reply || lastMsg?.role === 'agent';
  const st = thread.state || (thread.has_new ? 'pending' : 'answered');
  const isPending = st === 'pending';

  return (
    <div
      className={`w-full p-3 border-b border-gray-800/70 hover:bg-gray-800/40 transition-colors flex items-start gap-2 ${
        selected ? 'bg-gray-800/70 border-l-2 border-l-indigo-500' : ''
      } ${checked ? 'bg-indigo-900/20' : ''}`}
    >
      {showCheckbox && (
        <button
          type="button"
          data-testid={`thread-check-${thread.thread_id}`}
          onClick={(e) => { e.stopPropagation(); onToggleCheck?.(thread.thread_id); }}
          className="mt-1 text-gray-500 hover:text-indigo-400 transition-colors flex-shrink-0"
          title={checked ? 'Deseleccionar' : 'Seleccionar'}
        >
          {checked ? <CheckSquare size={16} className="text-indigo-400" /> : <Square size={16} />}
        </button>
      )}
      <button
        type="button"
        data-testid={`thread-card-${thread.thread_id}`}
        onClick={onClick}
        className="flex-1 text-left min-w-0"
      >
      <div className="flex items-start gap-2.5">
        <div className="relative">
          <Avatar name={name} />
          {isPending && (
            <span className="absolute -top-0.5 -right-0.5 w-3 h-3 bg-amber-500 rounded-full border-2 border-gray-900" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-1 mb-0.5">
            <span className="text-sm font-medium text-white truncate">{name}</span>
            <div className="flex items-center gap-1 flex-shrink-0">
              {otherViewers && otherViewers.length > 0 && <ViewersStack viewers={otherViewers} />}
              <span className="text-[11px] text-gray-500">{timeAgo(lastMsg?.created_at)}</span>
            </div>
          </div>
          {thread.external_order_id && thread.external_order_id !== '0' && (
            <div className="text-[11px] text-gray-500 flex items-center gap-1 mb-0.5">
              <Hash size={9} /> Pedido {thread.order_context?.reference || thread.external_order_id}
            </div>
          )}
          <p className={`text-xs truncate ${isAgent ? 'text-indigo-400' : 'text-gray-400'}`}>
            {isAgent && <span className="mr-1">Tú:</span>}
            {preview}
          </p>
          <div className="flex items-center gap-2 mt-1">
            {isPending && (
              <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-500/20 text-amber-400 border border-amber-500/30">
                {thread.new_count > 1 ? `${thread.new_count} nuevos` : 'Por responder'}
              </span>
            )}
            {st === 'answered' && (
              <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-500/30">
                Respondido
              </span>
            )}
            {st === 'closed' && (
              <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-gray-600/30 text-gray-400 border border-gray-600/40">
                Cerrado
              </span>
            )}
            <span className="text-[10px] text-gray-600">{thread.total_count} msg</span>
          </div>
        </div>
      </div>
      </button>
    </div>
  );
}

// ── Order context card ────────────────────────────────────────

function OrderContextCard({ ctx, orderId }) {
  if (!ctx || !Object.keys(ctx).length) {
    if (!orderId || orderId === '0') return null;
    return (
      <div className="bg-gray-800/40 border border-gray-700/40 rounded-lg p-3 mb-4">
        <div className="text-xs text-gray-500 flex items-center gap-1"><Hash size={10} /> Pedido {orderId}</div>
      </div>
    );
  }
  return (
    <div className="bg-gray-800/40 border border-gray-700/40 rounded-lg p-3 mb-4">
      <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2 flex items-center gap-1.5">
        <Package size={11} /> Pedido
      </h4>
      <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
        {ctx.reference && <div><div className="text-[10px] text-gray-500">Referencia</div><div className="text-xs text-white font-mono">{ctx.reference}</div></div>}
        {ctx.status && <div><div className="text-[10px] text-gray-500">Estado</div><div className="text-xs text-emerald-400">{ctx.status}</div></div>}
        {ctx.tracking_number && <div><div className="text-[10px] text-gray-500 flex items-center gap-1"><Truck size={9} /> Tracking</div><div className="text-xs text-white font-mono">{ctx.tracking_number}</div></div>}
        {ctx.date && <div><div className="text-[10px] text-gray-500 flex items-center gap-1"><Calendar size={9} /> Fecha</div><div className="text-xs text-white">{ctx.date}</div></div>}
        {ctx.total && <div><div className="text-[10px] text-gray-500">Total</div><div className="text-xs text-white">{ctx.total}€</div></div>}
      </div>
    </div>
  );
}

// ── Single message bubble ─────────────────────────────────────

function MessageBubble({ msg }) {
  const isAgent = msg.is_agent_reply || msg.role === 'agent';
  const isHistorical = msg.is_historical;

  if (isAgent) {
    return (
      <div className="flex justify-end mb-3">
        <div className="max-w-[75%] min-w-0">
          <div className="text-[10px] text-gray-500 text-right mb-0.5">
            {msg.replied_by?.name || (msg.replied_by?.source === 'prestashop' ? 'Equipo (PrestaShop)' : 'Agente')} · {timeAgo(msg.created_at || msg.date_sent)}
            {msg.sent_to_ps && <span className="ml-1 text-emerald-500">· PS ✓</span>}
          </div>
          <div className="bg-indigo-900/50 border border-indigo-700/50 text-indigo-100 px-3 py-2 rounded-2xl rounded-tr-sm text-sm leading-relaxed break-words">
            {msg.message || msg.agent_reply}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-start gap-2 mb-3">
      <Avatar name={msg.customer_name || msg.customer_email || 'C'} size="sm" />
      <div className="max-w-[75%] min-w-0">
        <div className="text-[10px] text-gray-500 mb-0.5 flex items-center gap-1">
          Cliente · {timeAgo(msg.created_at || msg.date_sent)}
          {isHistorical && (
            <span className="text-gray-600 border border-gray-700 px-1.5 rounded-full text-[9px]">Histórico</span>
          )}
          {msg.classification === 'important' && (
            <span className="text-rose-400 flex items-center gap-0.5"><AlertTriangle size={9} /> Requiere agente</span>
          )}
        </div>
        <div className={`px-3 py-2 rounded-2xl rounded-tl-sm text-sm leading-relaxed break-words ${
          isHistorical ? 'bg-gray-800/50 text-gray-400 border border-gray-700/30' : 'bg-gray-800 text-gray-100'
        }`}>
          {msg.message}
        </div>
        {/* AI response inline */}
        {msg.ai_response && (
          <div className="mt-1.5 ml-1">
            <div className="text-[10px] text-indigo-400 flex items-center gap-1 mb-0.5">
              <Bot size={9} /> IA respondió {msg.ai_response_sent ? '· enviado a PS ✓' : '· no enviado'}
            </div>
            <div className="bg-indigo-950/40 border border-indigo-800/30 text-indigo-200/80 px-3 py-2 rounded-lg text-xs leading-relaxed italic">
              {msg.ai_response}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Thread detail ─────────────────────────────────────────────

function ThreadDetail({ threadId, orgId, onReplied, otherViewers }) {
  const [thread, setThread] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [refreshingEmail, setRefreshingEmail] = useState(false);
  const [stating, setStating] = useState(false);
  const bottomRef = useRef(null);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const r = await api.get(`/api/order-messages/threads/${threadId}${orgId ? `?organization_id=${orgId}` : ''}`);
      setThread(r.data);
    } catch { if (!silent) toast.error('Error al cargar hilo'); }
    finally { if (!silent) setLoading(false); }
  }, [threadId, orgId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const iv = setInterval(() => { load(true); }, 15000);
    return () => clearInterval(iv);
  }, [load]);
  useEffect(() => {
    if (bottomRef.current) bottomRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [thread?.messages?.length]);

  const handleReply = async () => {
    if (!reply.trim() || sending) return;
    setSending(true);
    try {
      const res = await api.post(`/api/order-messages/threads/${threadId}/reply${orgId ? `?organization_id=${orgId}` : ''}`, { reply: reply.trim() });
      setReply('');
      await load();
      onReplied?.();
      if (res.data?.sent_to_ps === false) {
        toast.warning('Guardado, pero NO se pudo publicar en PrestaShop. Revisa la conexión.');
      } else if (!res.data?.ps_employee_id) {
        toast.warning('Enviado a PrestaShop, pero sin ID de empleado: aparecerá como anónimo. Asigna tu "ID PS" en Equipo o el ID por defecto en Ajustes.', { duration: 7000 });
      } else {
        toast.success('Respuesta enviada a PrestaShop');
      }
    } catch { toast.error('Error al enviar'); }
    finally { setSending(false); }
  };

  const handleRefreshEmail = async () => {
    setRefreshingEmail(true);
    try {
      const r = await api.post(
        `/api/order-messages/threads/${threadId}/refresh-email${orgId ? `?organization_id=${orgId}` : ''}`
      );
      toast.success(`Email actualizado a ${r.data.customer_email}`);
      await load();
      onReplied?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo actualizar el email desde PS');
    } finally { setRefreshingEmail(false); }
  };

  const useAiDraft = () => {
    if (thread?.ai_draft) setReply(thread.ai_draft);
  };

  const setThreadState = async (state) => {
    if (stating) return;
    setStating(true);
    try {
      await api.post(
        `/api/order-messages/threads/${threadId}/state${orgId ? `?organization_id=${orgId}` : ''}`,
        { state }
      );
      toast.success(
        state === 'answered' ? 'Marcado como respondido'
        : state === 'closed' ? 'Ticket cerrado'
        : 'Ticket reabierto'
      );
      await load();
      onReplied?.();
    } catch {
      toast.error('No se pudo actualizar el estado');
    } finally { setStating(false); }
  };

  if (loading) return (
    <div className="flex-1 flex items-center justify-center text-gray-500">
      <RefreshCw size={18} className="animate-spin mr-2" /> Cargando hilo...
    </div>
  );
  if (!thread) return null;

  const hasPending = thread.messages?.some(m => m.status === 'pending_agent' || m.status === 'pending_ai');

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-800 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Avatar name={thread.customer_name || thread.customer_email} />
          <div>
            <div className="text-sm font-semibold text-white">{thread.customer_name || 'Cliente'}</div>
            {thread.customer_email && (
              <div className="text-xs text-gray-400 flex items-center gap-1">
                <Mail size={10} /> {thread.customer_email}
                <button
                  data-testid="refresh-email-btn"
                  onClick={handleRefreshEmail}
                  disabled={refreshingEmail}
                  title="Corregir email desde PrestaShop"
                  className="ml-1 text-gray-600 hover:text-indigo-400 transition-colors disabled:opacity-40"
                >
                  <RefreshCw size={10} className={refreshingEmail ? 'animate-spin' : ''} />
                </button>
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
        {hasPending && (
          <span className="text-xs px-2 py-1 rounded-full bg-amber-500/15 text-amber-400 border border-amber-500/30 flex items-center gap-1">
            <AlertTriangle size={11} /> Pendiente de respuesta
          </span>
        )}
        <div className="flex items-center gap-1.5">
          <button
            data-testid="thread-mark-answered-btn"
            onClick={() => setThreadState('answered')}
            disabled={stating}
            title="Marcar como respondido"
            className="text-xs px-2.5 py-1 rounded-md bg-emerald-600/15 text-emerald-400 border border-emerald-600/30 hover:bg-emerald-600/25 transition-colors flex items-center gap-1 disabled:opacity-40"
          >
            <CheckCircle2 size={12} /> Respondido
          </button>
          <button
            data-testid="thread-close-btn"
            onClick={() => setThreadState('closed')}
            disabled={stating}
            title="Cerrar ticket"
            className="text-xs px-2.5 py-1 rounded-md bg-gray-700/40 text-gray-300 border border-gray-600/40 hover:bg-gray-700/60 transition-colors flex items-center gap-1 disabled:opacity-40"
          >
            <Lock size={12} /> Cerrar
          </button>
          <button
            data-testid="thread-reopen-btn"
            onClick={() => setThreadState('open')}
            disabled={stating}
            title="Reabrir ticket"
            className="text-xs px-2 py-1 rounded-md text-gray-500 hover:text-gray-300 transition-colors flex items-center gap-1 disabled:opacity-40"
          >
            <RotateCcw size={12} />
          </button>
        </div>
        </div>
      </div>

      {/* Messages */}
      {otherViewers && otherViewers.length > 0 && <ViewersBanner viewers={otherViewers} />}
      {thread.customer_id && (
        <CustomerThreadsBanner customerId={thread.customer_id} currentType="order_message" currentId={threadId} />
      )}
      <div className="flex-1 overflow-y-auto overflow-x-hidden px-4 py-4">
        <OrderContextCard ctx={thread.order_context} orderId={thread.external_order_id} />
        {thread.messages?.map((msg, i) => (
          <MessageBubble key={msg.message_id || i} msg={msg} />
        ))}
        <div ref={bottomRef} />
      </div>

      {/* AI Draft — proposed response for agent review */}
      {thread.ai_draft && !reply && (
        <div className="mx-4 mb-2 rounded-lg border border-indigo-500/30 bg-indigo-500/5 p-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-indigo-400 flex items-center gap-1.5">
              <Bot size={12} /> Borrador propuesto por IA
            </span>
            <button
              data-testid="use-ai-draft-btn"
              onClick={useAiDraft}
              className="text-xs px-2.5 py-1 rounded-md bg-indigo-600 hover:bg-indigo-500 text-white transition-colors flex items-center gap-1"
            >
              <CheckCircle2 size={11} /> Usar esta respuesta
            </button>
          </div>
          <p className="text-xs text-gray-300 whitespace-pre-wrap leading-relaxed line-clamp-4">{thread.ai_draft}</p>
        </div>
      )}

      {/* Reply */}
      <div className="p-4 border-t border-gray-800">
        <div className="flex gap-2">
          <textarea
            data-testid="thread-reply-input"
            value={reply}
            onChange={e => setReply(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && e.ctrlKey) handleReply(); }}
            placeholder="Escribe tu respuesta... (Ctrl+Enter para enviar)"
            rows={3}
            className="flex-1 resize-none bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-indigo-500 transition-colors"
          />
          <button
            data-testid="thread-send-btn"
            onClick={handleReply}
            disabled={!reply.trim() || sending}
            className="px-3 py-2 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-40 text-white rounded-lg transition-colors flex items-center justify-center"
          >
            {sending ? <RefreshCw size={16} className="animate-spin" /> : <Send size={16} />}
          </button>
        </div>
        <p className="text-[11px] text-gray-600 mt-1.5">La respuesta se publicará también en PrestaShop</p>
      </div>
    </div>
  );
}

// ── Settings modal ────────────────────────────────────────────

function SettingsModal({ settings, onSave, onClose }) {
  const [form, setForm] = useState(settings);
  const [kwInput, setKwInput] = useState('');
  const [saving, setSaving] = useState(false);

  const addKw = () => {
    const kw = kwInput.trim();
    if (kw && !form.escalate_keywords.includes(kw)) {
      setForm(p => ({ ...p, escalate_keywords: [...p.escalate_keywords, kw] }));
      setKwInput('');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-gray-900 border border-gray-700 rounded-2xl w-full max-w-lg shadow-2xl">
        <div className="p-5 border-b border-gray-800 flex items-center justify-between">
          <h2 className="text-base font-semibold text-white flex items-center gap-2"><Settings size={16} /> Configuración IA</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-5">
          {/* Auto-reply toggle REMOVED — auto-send is permanently disabled after incident */}
          <div className="flex items-start gap-3 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
            <AlertTriangle size={15} className="text-amber-400 mt-0.5 flex-shrink-0" />
            <div>
              <div className="text-xs font-semibold text-amber-300">Auto-respuesta desactivada</div>
              <div className="text-xs text-gray-400 mt-0.5">
                La IA genera borradores de respuesta para que el agente los revise y envíe manualmente. El envío automático está deshabilitado.
              </div>
            </div>
          </div>
          <div>
            <label className="text-sm font-medium text-white mb-1.5 block">Idioma de respuesta</label>
            <select value={form.language} onChange={e => setForm(p => ({ ...p, language: e.target.value }))}
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-indigo-500">
              {[['es','Español'],['en','English'],['fr','Français'],['de','Deutsch'],['it','Italiano'],['pt','Português']].map(([v,l]) => (
                <option key={v} value={v}>{l}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-sm font-medium text-white mb-1.5 block">ID empleado PrestaShop</label>
            <input type="number" min="0" value={form.ps_employee_id || 0}
              onChange={e => setForm(p => ({ ...p, ps_employee_id: parseInt(e.target.value) || 0 }))}
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-indigo-500" />
            <p className="text-xs text-gray-500 mt-1">0 = anónimo. Las respuestas aparecerán firmadas por ese empleado en PS.</p>
          </div>
          <div>
            <label className="text-sm font-medium text-white mb-1.5 block">Instrucciones personalizadas IA</label>
            <textarea value={form.ai_instructions} onChange={e => setForm(p => ({ ...p, ai_instructions: e.target.value }))}
              rows={3} placeholder="Ej: Menciona siempre nuestro teléfono..."
              className="w-full resize-none bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-indigo-500" />
          </div>
          <div>
            <label className="text-sm font-medium text-white mb-1.5 block">Palabras que escalan al agente</label>
            <div className="flex gap-2 mb-2">
              <input value={kwInput} onChange={e => setKwInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addKw(); } }}
                placeholder="Añadir palabra clave..."
                className="flex-1 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-indigo-500" />
              <button onClick={addKw} className="px-3 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg text-sm border border-gray-600">Añadir</button>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {form.escalate_keywords.map(kw => (
                <span key={kw} className="inline-flex items-center gap-1 text-xs px-2 py-1 bg-rose-900/30 text-rose-300 border border-rose-500/20 rounded-full">
                  {kw}
                  <button onClick={() => setForm(p => ({ ...p, escalate_keywords: p.escalate_keywords.filter(k => k !== kw) }))}><X size={10} /></button>
                </span>
              ))}
            </div>
          </div>
        </div>
        <div className="p-5 border-t border-gray-800 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-400 hover:text-white">Cancelar</button>
          <button onClick={async () => { setSaving(true); try { await onSave(form); onClose(); } finally { setSaving(false); } }}
            disabled={saving}
            className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white rounded-lg text-sm font-medium">
            {saving ? 'Guardando...' : 'Guardar'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────

export default function OrderMessagesPage() {
  const { organization } = useOrganization();
  const orgId = organization?.id;
  const [searchParams, setSearchParams] = useSearchParams();

  const [threads, setThreads]             = useState([]);
  const [selectedThread, setSelectedThread] = useState(null);
  const [loading, setLoading]             = useState(true);
  const [filter, setFilter]               = useState('pending'); // 'pending' | 'answered' | 'all'
  const [search, setSearch]               = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [settings, setSettings]           = useState(null);
  const [showSettings, setShowSettings]   = useState(false);
  const [syncing, setSyncing]             = useState(false);
  const [syncStatus, setSyncStatus]       = useState(null); // {status, imported, error}
  const syncPollRef                       = useRef(null);
  const [wsTest, setWsTest]               = useState(null);  // null | {ok, resources, failed, suggestion, api_key_preview}
  const [wsTestLoading, setWsTestLoading] = useState(false);

  // ── Selección múltiple para acciones en bloque ──
  const [checkedIds, setCheckedIds] = useState(() => new Set());
  const [bulkBusy, setBulkBusy]     = useState(false);
  const toggleCheck = useCallback((tid) => {
    setCheckedIds(prev => {
      const next = new Set(prev);
      if (next.has(tid)) next.delete(tid); else next.add(tid);
      return next;
    });
  }, []);
  const clearChecks = useCallback(() => setCheckedIds(new Set()), []);
  // Al cambiar filtro o búsqueda, limpiamos la selección
  useEffect(() => { clearChecks(); }, [filter, debouncedSearch, clearChecks]);

  // ── Presencia (qué agentes están viendo qué hilo) ──
  const selectedResourceKey = selectedThread ? `order:${selectedThread}` : '';
  const { viewersByResource, viewersOthersOnCurrent, selfAgentId } = usePresence({
    orgId,
    resourceKey: selectedResourceKey,
  });

  const loadThreads = useCallback(async (silent = false) => {
    if (!orgId) return;
    if (!silent) setLoading(true);
    try {
      const q = debouncedSearch.trim() ? `&search=${encodeURIComponent(debouncedSearch.trim())}` : '';
      const r = await api.get(`/api/order-messages/threads?filter=${filter}&organization_id=${orgId}${q}`);
      setThreads(r.data.threads || []);
    } catch { if (!silent) toast.error('Error al cargar hilos'); }
    finally { if (!silent) setLoading(false); }
  }, [orgId, filter, debouncedSearch]);

  const loadSettings = useCallback(async () => {
    if (!orgId) return;
    try {
      const r = await api.get(`/api/order-messages/settings/config?organization_id=${orgId}`);
      setSettings(r.data);
    } catch {
      // Si no hay config previa, usar defaults para que el modal pueda abrirse
      setSettings({
        auto_reply_enabled: false,
        language: 'es',
        ps_employee_id: 0,
        ai_instructions: '',
        escalate_keywords: [],
      });
    }
  }, [orgId]);

  useEffect(() => { loadThreads(); loadSettings(); }, [loadThreads, loadSettings]);

  // Deep-link: ?thread=<id> (ej. desde el banner "Este cliente tiene más conversaciones")
  useEffect(() => {
    const wanted = searchParams.get('thread');
    if (!wanted) return;
    setSelectedThread(wanted);
    setSearchParams(prev => { const p = new URLSearchParams(prev); p.delete('thread'); return p; }, { replace: true });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Auto-refresco: refleja mensajes/respuestas nuevas de PrestaShop sin recargar la página
  useEffect(() => {
    const iv = setInterval(() => { loadThreads(true); }, 15000);
    return () => clearInterval(iv);
  }, [loadThreads]);

  // Debounce del buscador (300ms)
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  // Poll sync status while running
  const startSyncPolling = useCallback(() => {
    if (syncPollRef.current) return;
    syncPollRef.current = setInterval(async () => {
      try {
        const r = await api.get(`/api/order-messages/sync/status?organization_id=${orgId}`);
        const s = r.data;
        setSyncStatus(s);
        if (s.status !== 'running') {
          clearInterval(syncPollRef.current);
          syncPollRef.current = null;
          setSyncing(false);
          if (s.status === 'completed') {
            toast.success(`Importados ${s.imported} mensajes históricos`);
            await loadThreads();
          } else if (s.status === 'error') {
            const errMsg = s.error || 'Fallo desconocido. Usa el botón "Test WS" (icono Wifi) para diagnosticar la conexión con PrestaShop.';
            toast.error(`Error en sync: ${errMsg}`, { duration: 8000 });
          }
        }
      } catch {
        clearInterval(syncPollRef.current);
        syncPollRef.current = null;
        setSyncing(false);
      }
    }, 2000);
  }, [orgId, loadThreads]);

  useEffect(() => () => { if (syncPollRef.current) clearInterval(syncPollRef.current); }, []);

  const handleWsTest = async () => {
    setWsTestLoading(true);
    setWsTest(null);
    try {
      const r = await api.get(`/api/order-messages/ws-test?organization_id=${orgId}`);
      setWsTest(r.data);
    } catch (e) {
      setWsTest({ ok: false, shop_reachable: false, failed: [], suggestion: e?.response?.data?.detail || 'No se pudo conectar con PrestaShop' });
    } finally { setWsTestLoading(false); }
  };

  const handleSync = async () => {
    setSyncing(true);
    setSyncStatus({ status: 'running', imported: 0 });
    try {
      await api.post(`/api/order-messages/sync?organization_id=${orgId}`, { limit: 500, days: 7 });
      startSyncPolling();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al iniciar sync');
      setSyncing(false);
      setSyncStatus(null);
    }
  };

  const saveSettings = async (s) => {
    await api.put(`/api/order-messages/settings/config?organization_id=${orgId}`, s);
    setSettings(s);
    toast.success('Configuración guardada');
  };

  const applyBulkState = async (state) => {
    const ids = Array.from(checkedIds);
    if (!ids.length || bulkBusy) return;
    const label = state === 'answered' ? 'respondidos' : state === 'closed' ? 'cerrados' : 'reabiertos';
    setBulkBusy(true);
    try {
      await api.post(
        `/api/order-messages/threads/bulk-state?organization_id=${orgId}`,
        { thread_ids: ids, state }
      );
      toast.success(`${ids.length} hilo(s) marcados como ${label}`);
      clearChecks();
      await loadThreads(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudieron actualizar');
    } finally {
      setBulkBusy(false);
    }
  };

  const pendingCount = threads.filter(t => (t.state || (t.has_new ? 'pending' : 'answered')) === 'pending').length;

  return (
    <div className="flex h-screen overflow-hidden bg-gray-950">
      {/* ── Left: thread list ─────────────────────────── */}
      <div className="w-80 flex-shrink-0 border-r border-gray-800 flex flex-col overflow-hidden">
        {/* Header */}
        <div className="px-4 py-3 border-b border-gray-800">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <h2 className="text-sm font-semibold text-white">Mensajes de pedido</h2>
              {pendingCount > 0 && (
                <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-amber-500 text-white">
                  {pendingCount}
                </span>
              )}
            </div>
            <div className="flex items-center gap-1.5">
              <button onClick={handleWsTest} disabled={wsTestLoading} title="Test conexión WebService PS"
                data-testid="ws-test-btn"
                className="text-gray-400 hover:text-white p-1.5 rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-50">
                {wsTestLoading ? <RefreshCw size={15} className="animate-spin" /> : <Wifi size={15} />}
              </button>
              <button onClick={handleSync} disabled={syncing} title="Importar mensajes de la última semana desde PrestaShop"
                data-testid="bulk-sync-btn"
                className="text-gray-400 hover:text-white p-1.5 rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-50 relative">
                <Download size={15} className={syncing ? 'animate-bounce' : ''} />
              </button>
              {syncing && syncStatus && (
                <span className="text-[10px] text-indigo-400 whitespace-nowrap" data-testid="sync-progress">
                  {syncStatus.imported > 0 ? `${syncStatus.imported} msgs…` : 'Importando…'}
                </span>
              )}
              <button onClick={loadThreads} data-testid="refresh-btn"
                className="text-gray-400 hover:text-white p-1.5 rounded-lg hover:bg-gray-800 transition-colors">
                <RefreshCw size={15} />
              </button>
              <button onClick={() => setShowSettings(true)} data-testid="settings-btn"
                className="text-gray-400 hover:text-white p-1.5 rounded-lg hover:bg-gray-800 transition-colors">
                <Settings size={15} />
              </button>
            </div>
          </div>
          {/* WS Test result banner */}
          {wsTest && (
            <div data-testid="ws-test-result"
              className={`mt-2 rounded-lg p-2 text-[11px] ${wsTest.ok ? 'bg-green-900/40 text-green-300' : 'bg-red-900/40 text-red-300'}`}>
              <div className="flex items-start gap-1.5">
                {wsTest.ok ? <Wifi size={12} className="mt-0.5 shrink-0" /> : <WifiOff size={12} className="mt-0.5 shrink-0" />}
                <div className="flex-1 min-w-0">
                  {wsTest.ok ? (
                    <span>Conexión OK — todos los permisos están correctos</span>
                  ) : wsTest.shop_reachable === false ? (
                    <span>No se puede conectar con PrestaShop. Verifica la URL y clave API en la configuración del conector.</span>
                  ) : (
                    <>
                      <p className="font-medium mb-1">Permisos WS faltantes:</p>
                      <ul className="space-y-0.5">
                        {Object.entries(wsTest.resources || {}).map(([res, info]) => (
                          <li key={res} className="flex items-center gap-1">
                            {info.ok
                              ? <CheckCircle2 size={10} className="text-green-400 shrink-0" />
                              : <AlertCircle  size={10} className="text-red-400 shrink-0" />}
                            <span className={info.ok ? 'text-green-400' : 'text-red-300'}>{res}</span>
                            {!info.ok && <span className="text-gray-500 truncate"> — {info.error}</span>}
                          </li>
                        ))}
                      </ul>
                      <p className="mt-1.5 text-yellow-300/90">PS Admin → WebService → clave "Sera AI" → activa GET para los recursos marcados en rojo</p>
                    </>
                  )}
                  {wsTest.api_key_preview && (
                    <p className="mt-1 text-gray-400">Clave usada: <code className="font-mono">{wsTest.api_key_preview}</code></p>
                  )}
                </div>
                <button onClick={() => setWsTest(null)} className="text-gray-500 hover:text-gray-300 shrink-0"><X size={11}/></button>
              </div>
            </div>
          )}
          {/* Sync error banner */}
          {syncStatus?.status === 'error' && (
            <div data-testid="sync-error-banner" className="mt-2 bg-red-900/40 rounded-lg p-2 text-[11px] text-red-300">
              <div className="flex items-start gap-1.5">
                <AlertCircle size={12} className="mt-0.5 shrink-0" />
                <span className="flex-1">{syncStatus.error || 'Error desconocido en el sync'}</span>
                <button onClick={() => setSyncStatus(null)} className="text-gray-500 hover:text-gray-300 shrink-0"><X size={11}/></button>
              </div>
              <button
                onClick={async () => {
                  await api.post(`/api/order-messages/sync/reset?organization_id=${orgId}`).catch(()=>{});
                  setSyncStatus(null);
                  handleSync();
                }}
                className="mt-1.5 ml-5 text-[10px] text-indigo-400 hover:text-indigo-300 underline"
                data-testid="sync-retry-btn"
              >Reintentar sync</button>
            </div>
          )}
          {/* Buscador */}
          <div className="relative mt-2">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none" />
            <input
              data-testid="thread-search-input"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Buscar por pedido, email, cliente o texto..."
              className="w-full bg-gray-800 border border-gray-700 rounded-lg pl-8 pr-8 py-1.5 text-xs text-white placeholder-gray-500 focus:outline-none focus:border-indigo-500 transition-colors"
            />
            {search && (
              <button
                data-testid="thread-search-clear"
                onClick={() => setSearch('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white"
                title="Limpiar búsqueda"
              >
                <X size={13} />
              </button>
            )}
          </div>
          {/* Filter tabs */}
          <div className="flex gap-1 mt-2">
            {[['pending','Por responder'],['answered','Respondidos'],['closed','Cerrados'],['all','Todos']].map(([v, l]) => (
              <button key={v} onClick={() => setFilter(v)}
                data-testid={`filter-${v}`}
                className={`flex-1 text-xs py-1 rounded-lg transition-colors ${
                  filter === v ? 'bg-indigo-600 text-white' : 'text-gray-500 hover:text-gray-300 hover:bg-gray-800'
                }`}>
                {l}
              </button>
            ))}
          </div>
        </div>

        {/* Bulk selection toolbar */}
        {threads.length > 0 && (
          <div
            data-testid="bulk-toolbar"
            className="px-3 py-2 border-b border-gray-800 flex items-center gap-2 bg-gray-900/40"
          >
            <button
              type="button"
              data-testid="bulk-select-all"
              onClick={() => {
                if (checkedIds.size === threads.length) {
                  clearChecks();
                } else {
                  setCheckedIds(new Set(threads.map(t => t.thread_id)));
                }
              }}
              className="text-gray-400 hover:text-indigo-400 transition-colors"
              title={checkedIds.size === threads.length ? 'Deseleccionar todos' : 'Seleccionar todos'}
            >
              {checkedIds.size > 0 && checkedIds.size === threads.length
                ? <CheckSquare size={16} className="text-indigo-400" />
                : <Square size={16} />}
            </button>
            <span className="text-[11px] text-gray-500 flex-1">
              {checkedIds.size > 0 ? `${checkedIds.size} seleccionado(s)` : 'Selecciona hilos'}
            </span>
            {checkedIds.size > 0 && (
              <>
                <button
                  type="button"
                  data-testid="bulk-mark-answered"
                  disabled={bulkBusy}
                  onClick={() => applyBulkState('answered')}
                  className="text-[11px] px-2 py-1 rounded-md bg-emerald-600/15 text-emerald-400 border border-emerald-600/30 hover:bg-emerald-600/25 transition-colors flex items-center gap-1 disabled:opacity-40"
                  title="Marcar seleccionados como respondidos"
                >
                  <CheckCircle2 size={11} /> Respondidos
                </button>
                <button
                  type="button"
                  data-testid="bulk-mark-closed"
                  disabled={bulkBusy}
                  onClick={() => applyBulkState('closed')}
                  className="text-[11px] px-2 py-1 rounded-md bg-gray-700/40 text-gray-300 border border-gray-600/40 hover:bg-gray-700/60 transition-colors flex items-center gap-1 disabled:opacity-40"
                  title="Cerrar tickets seleccionados"
                >
                  <Lock size={11} /> Cerrar
                </button>
                <button
                  type="button"
                  data-testid="bulk-clear"
                  disabled={bulkBusy}
                  onClick={clearChecks}
                  className="text-gray-500 hover:text-gray-300 transition-colors"
                  title="Limpiar selección"
                >
                  <X size={13} />
                </button>
              </>
            )}
          </div>
        )}

        {/* Thread list */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center h-32 text-gray-500">
              <RefreshCw size={16} className="animate-spin mr-2" /> Cargando...
            </div>
          ) : threads.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-40 text-gray-600 px-4 text-center">
              <MessageSquare size={24} className="mb-2 opacity-40" />
              <p className="text-xs">
                {debouncedSearch.trim()
                  ? `Sin resultados para "${debouncedSearch.trim()}"`
                  : filter === 'pending' ? 'No hay mensajes por responder. 🎉'
                  : filter === 'answered' ? 'Aún no hay mensajes respondidos.'
                  : filter === 'closed' ? 'No hay tickets cerrados.'
                  : 'No hay conversaciones.'}
              </p>
              {filter === 'answered' && !debouncedSearch.trim() && (
                <button
                  data-testid="restore-answered-btn"
                  onClick={async () => {
                    try {
                      const r = await api.post(`/api/order-messages/restore-answered?organization_id=${orgId}`);
                      const d = r.data;
                      if (d.total_marked_answered > 0) {
                        toast.success(`${d.total_marked_answered} hilos restaurados en Respondidos`);
                        loadThreads();
                      } else {
                        toast.info('No se encontraron hilos con respuesta de agente pendientes de restaurar.');
                      }
                    } catch { toast.error('Error al restaurar'); }
                  }}
                  className="mt-3 text-xs text-indigo-400 hover:text-indigo-300 flex items-center gap-1 border border-indigo-800/50 rounded-lg px-3 py-1.5 hover:bg-indigo-900/20 transition-colors"
                >
                  <RefreshCw size={12} /> Restaurar historial respondidos
                </button>
              )}
              {filter === 'all' && !debouncedSearch.trim() && (
                <button onClick={handleSync} className="mt-2 text-xs text-indigo-400 hover:text-indigo-300 flex items-center gap-1">
                  <Download size={12} /> Importar de PrestaShop
                </button>
              )}
            </div>
          ) : threads.map(t => (
            <ThreadCard
              key={t.thread_id}
              thread={t}
              selected={selectedThread === t.thread_id}
              onClick={() => setSelectedThread(t.thread_id)}
              otherViewers={getOtherViewers(viewersByResource, `order:${t.thread_id}`, selfAgentId)}
              showCheckbox
              checked={checkedIds.has(t.thread_id)}
              onToggleCheck={toggleCheck}
            />
          ))}
        </div>
      </div>

      {/* ── Right: thread detail ────────────────────── */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {selectedThread ? (
          <ThreadDetail
            threadId={selectedThread}
            orgId={orgId}
            onReplied={loadThreads}
            otherViewers={viewersOthersOnCurrent}
          />
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center text-gray-600">
            <MessageSquare size={32} className="mb-3 opacity-30" />
            <p className="text-sm">Selecciona una conversación</p>
          </div>
        )}
      </div>

      {/* Settings modal */}
      {showSettings && (
        <SettingsModal
          settings={settings || { auto_reply_enabled: false, language: 'es', ps_employee_id: 0, ai_instructions: '', escalate_keywords: [] }}
          onSave={saveSettings}
          onClose={() => setShowSettings(false)}
        />
      )}
    </div>
  );
}
